export interface DailyStats {
  date: string; // "YYYY-MM-DD"
  deposits: number;
  withdrawals: number;
}

export interface DailyVolume {
  date: string; // "YYYY-MM-DD"
  depositVolume: number;  // human-readable token units
  withdrawalVolume: number;
}

export type ChartPeriod = 'day' | 'week' | 'month';

interface DecodedInput {
  parameters: Array<{ name: string; type: string; value: string }>;
}

interface BlockscoutTransaction {
  hash: string;
  timestamp: string; // ISO 8601
  method: string | null;
  to: { hash: string } | null;
  from: { hash: string };
  status: string; // "ok" for successful
  value: string; // native token amount in wei
  decoded_input: DecodedInput | null;
}

function toDateString(isoTimestamp: string): string {
  return isoTimestamp.slice(0, 10); // "YYYY-MM-DD"
}

function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function todayUTC(): string {
  return new Date().toISOString().slice(0, 10);
}

function toWeekStart(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00Z");
  const day = d.getUTCDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setUTCDate(d.getUTCDate() + diff);
  return d.toISOString().slice(0, 10);
}

function toMonthStart(dateStr: string): string {
  return dateStr.slice(0, 7) + "-01";
}

function bucketKey(dateStr: string, period: ChartPeriod): string {
  if (period === 'week') return toWeekStart(dateStr);
  if (period === 'month') return toMonthStart(dateStr);
  return dateStr;
}

// Extract amount from a transaction in human-readable token units.
// Prefer decoded `amount` (ERC-20 / PoD deposit|withdraw calldata).
// Fall back to tx.value for native COTI deposit() with no parameters.
function extractAmount(tx: BlockscoutTransaction, decimals: number): number {
  const param = tx.decoded_input?.parameters?.find(
    (p) => p.name === 'amount' && p.type === 'uint256'
  );
  const raw = param ? param.value : (tx.value ?? '0');
  try {
    const rawBig = BigInt(raw);
    if (rawBig === 0n) return 0;
    const divisor = BigInt(10 ** decimals);
    const whole = rawBig / divisor;
    const remainder = rawBig % divisor;
    return Number(whole) + Number(remainder) / 10 ** decimals;
  } catch {
    return 0;
  }
}

function classifyTx(tx: BlockscoutTransaction, bridge: string) {
  const method = tx.method?.toLowerCase() ?? null;
  const isDeposit =
    method === 'deposit' ||
    method === 'depositnative' ||
    (tx.method === null && tx.to !== null && tx.to.hash.toLowerCase() === bridge);
  const isWithdrawal =
    method === 'withdraw' ||
    method === 'ontokenreceived' ||
    method === 'requestwithdrawwithpermit' ||
    (tx.method === null && tx.from.hash.toLowerCase() === bridge);
  return { isDeposit, isWithdrawal };
}

export function groupStats(daily: DailyStats[], period: ChartPeriod): DailyStats[] {
  if (period === 'day') return daily;
  const buckets = new Map<string, { deposits: number; withdrawals: number }>();
  for (const entry of daily) {
    const key = bucketKey(entry.date, period);
    const existing = buckets.get(key) ?? { deposits: 0, withdrawals: 0 };
    existing.deposits += entry.deposits;
    existing.withdrawals += entry.withdrawals;
    buckets.set(key, existing);
  }
  return Array.from(buckets.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, counts]) => ({ date, ...counts }));
}

export function groupVolume(daily: DailyVolume[], period: ChartPeriod): DailyVolume[] {
  if (period === 'day') return daily;
  const buckets = new Map<string, { depositVolume: number; withdrawalVolume: number }>();
  for (const entry of daily) {
    const key = bucketKey(entry.date, period);
    const existing = buckets.get(key) ?? { depositVolume: 0, withdrawalVolume: 0 };
    existing.depositVolume += entry.depositVolume;
    existing.withdrawalVolume += entry.withdrawalVolume;
    buckets.set(key, existing);
  }
  return Array.from(buckets.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, v]) => ({ date, ...v }));
}

export function aggregateTransactionsByDay(
  transactions: BlockscoutTransaction[],
  bridgeAddress: string
): DailyStats[] {
  const bridge = bridgeAddress.toLowerCase();
  const counts = new Map<string, { deposits: number; withdrawals: number }>();

  for (const tx of transactions) {
    if (tx.status !== "ok") continue;
    const { isDeposit, isWithdrawal } = classifyTx(tx, bridge);
    if (!isDeposit && !isWithdrawal) continue;
    const date = toDateString(tx.timestamp);
    if (!counts.has(date)) counts.set(date, { deposits: 0, withdrawals: 0 });
    const entry = counts.get(date)!;
    if (isDeposit) entry.deposits++;
    if (isWithdrawal) entry.withdrawals++;
  }

  if (counts.size === 0) return [];

  const allDates = Array.from(counts.keys()).sort();
  const startDate = allDates[0];
  const endDate = todayUTC();
  const result: DailyStats[] = [];
  let current = startDate;
  while (current <= endDate) {
    const entry = counts.get(current);
    result.push({ date: current, deposits: entry?.deposits ?? 0, withdrawals: entry?.withdrawals ?? 0 });
    current = addDays(current, 1);
  }
  return result;
}

export function aggregateVolumeByDay(
  transactions: BlockscoutTransaction[],
  bridgeAddress: string,
  tokenDecimals: number
): DailyVolume[] {
  const bridge = bridgeAddress.toLowerCase();
  const volumes = new Map<string, { depositVolume: number; withdrawalVolume: number }>();

  for (const tx of transactions) {
    if (tx.status !== "ok") continue;
    const { isDeposit, isWithdrawal } = classifyTx(tx, bridge);
    if (!isDeposit && !isWithdrawal) continue;
    const date = toDateString(tx.timestamp);
    if (!volumes.has(date)) volumes.set(date, { depositVolume: 0, withdrawalVolume: 0 });
    const entry = volumes.get(date)!;
    const amount = extractAmount(tx, tokenDecimals);
    if (isDeposit) entry.depositVolume += amount;
    if (isWithdrawal) entry.withdrawalVolume += amount;
  }

  if (volumes.size === 0) return [];

  const allDates = Array.from(volumes.keys()).sort();
  const startDate = allDates[0];
  const endDate = todayUTC();
  const result: DailyVolume[] = [];
  let current = startDate;
  while (current <= endDate) {
    const entry = volumes.get(current);
    result.push({ date: current, depositVolume: entry?.depositVolume ?? 0, withdrawalVolume: entry?.withdrawalVolume ?? 0 });
    current = addDays(current, 1);
  }
  return result;
}
